import React, { useState } from "react";
import {
  GitBranch, Inbox, Layers, Filter, CornerDownRight, Paperclip,
  FileText, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown,
} from "lucide-react";
import { FILTERS } from "../../constants";
import { docTypeLabel, statusOf, synthParticipantResult, tagMeta } from "../../utils";
import type { RosterPerson } from "../../utils";
import { Pill } from "../common/Pill";
import { TauMeter } from "../common/TauMeter";
import { AgentResult, ParticipantMap } from "../common/AgentResult";
import { DownloadLink } from "../common/DownloadLink";
import type { CheckMap, ProcessedEntry } from "../../types";

export interface SpineCardEntry {
  p: ProcessedEntry;
  conf: number;
}

export interface SpineMatter {
  id: string;
  name: string;
}

export interface SpineColProps {
  mattersInScope: SpineMatter[];
  wsName: (id: string) => string;
  wsEntries: Record<string, SpineCardEntry[]>;
  activeWs: string;
  setActiveWs: (id: string) => void;
  filter: string;
  setFilter: (id: string) => void;
  showChecks: Record<string, boolean>;
  setShowChecks: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  expandedCheck: Record<string, boolean>;
  setExpandedCheck: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  thresholds: Record<string, number>;
  CM: CheckMap;
  cursor: number;
  total: number;
  /** entityId → display name, from the workstream entity rosters. */
  entityNames?: Record<string, string>;
  /** address → entity/role, for the participant-mapping fallback. */
  addressRoster?: Record<string, RosterPerson>;
}

/* Centre column — the "Spine Viewer". One card per processed email in the
   active workstream tab, expandable to show all checks that ran, their τ,
   their confidence and their raw output. */
export function SpineCol({
  mattersInScope, wsName, wsEntries, activeWs, setActiveWs, filter, setFilter,
  showChecks, setShowChecks, expandedCheck, setExpandedCheck, thresholds, CM, cursor, total,
  entityNames, addressRoster,
}: SpineColProps): JSX.Element {
  /* Per-card collapse: explicit toggles override the all-collapsed default. */
  const [cardCollapsed, setCardCollapsed] = useState<Record<string, boolean>>({});
  const [allCollapsed, setAllCollapsed] = useState<boolean>(false);
  const collapseAll = (v: boolean): void => {
    setAllCollapsed(v);
    setCardCollapsed({});
  };
  return (
    <div className="scol">
      <div className="scolhd">
        <GitBranch size={15} color="var(--muted)" />
        <span className="t">Spine Viewer</span>
        <span className="spacer" />
        <span className="mono tert" style={{ fontSize: 11 }}>
          catalog · clean · extract · append · tag
        </span>
      </div>
      <div className="progress">
        <div
          className={"f" + (total > 0 && cursor >= total ? " done" : "")}
          style={{ width: (total ? Math.min(cursor, total) / total * 100 : 0) + "%" }}
        />
      </div>
      <div className="wsrow">
        {[...mattersInScope.map((w) => w.id), "unassoc"].map((id) => (
          <button
            key={id}
            className={"wstab " + (activeWs === id ? "on" : "")}
            onClick={() => setActiveWs(id)}
          >
            {id === "unassoc" ? <Inbox size={13} /> : <Layers size={13} />}
            {wsName(id)}
            <span className="c">{(wsEntries[id] || []).length}</span>
          </button>
        ))}
      </div>
      <div className="filterrow">
        <Filter size={13} color="var(--tert)" />
        {FILTERS.map((f) => (
          <button key={f[0]} className={"fchip " + (filter === f[0] ? "on" : "")} onClick={() => setFilter(f[0])}>
            {f[1]}
          </button>
        ))}
        <span className="spacer" />
        <button
          className="fchip"
          title={allCollapsed ? "Expand all emails" : "Collapse all emails"}
          onClick={() => collapseAll(!allCollapsed)}
        >
          {allCollapsed ? <ChevronsUpDown size={11} /> : <ChevronsDownUp size={11} />}
          {allCollapsed ? " Expand all" : " Collapse all"}
        </button>
      </div>
      <div className="scolbody scroll">
        {(() => {
          const all = wsEntries[activeWs] || [];
          const shown = all.filter(
            ({ p }) => filter === "all" || p.R.tags.some((t) => t[0] === filter),
          );
          if (all.length === 0)
            return <div className="empty">No communications cataloged here.</div>;
          if (shown.length === 0) {
            const label = (FILTERS.find((f) => f[0] === filter) || ["", filter])[1];
            return (
              <div className="empty">
                No {String(label).toLowerCase()} items in this run yet.
                <br />
                <button className="lnk" onClick={() => setFilter("all")}>
                  Show all
                </button>
              </div>
            );
          }
          return null;
        })()}
        {(wsEntries[activeWs] || [])
          .filter(({ p }) => filter === "all" || p.R.tags.some((t) => t[0] === filter))
          .map(({ p, conf }, idx) => {
            const e = p.email;
            const R = p.R;
            const sc = showChecks[e.id + activeWs];
            const multi = R.catalogs.length > 1;
            const cardKey = e.id + activeWs;
            const isCollapsed = cardCollapsed[cardKey] ?? allCollapsed;
            return (
              <div className="scard" key={idx}>
                <div
                  className="sh"
                  style={{ cursor: "pointer", borderBottom: isCollapsed ? "none" : undefined }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={!isCollapsed}
                  onClick={() =>
                    setCardCollapsed((s) => ({ ...s, [cardKey]: !isCollapsed }))
                  }
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") {
                      ev.preventDefault();
                      setCardCollapsed((s) => ({ ...s, [cardKey]: !isCollapsed }));
                    }
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                    {isCollapsed ? (
                      <ChevronRight size={13} color="var(--tert)" style={{ flex: "none" }} />
                    ) : (
                      <ChevronDown size={13} color="var(--tert)" style={{ flex: "none" }} />
                    )}
                    <span className="stt">{e.subject}</span>
                  </span>
                  <span className="mono tert" style={{ fontSize: 11, flex: "none" }}>
                    #{e.id}
                  </span>
                </div>
                {!isCollapsed && (
                <div className="sb">
                  <div className="metaline">
                    <Pill color={conf >= 0.7 ? "--green" : "--amber"} label={"catalog " + conf.toFixed(2)} />
                    {multi && (
                      <Pill
                        color="--blue"
                        label={
                          "multi · " + R.catalogs.map((c) => wsName(c[0]).split(" ")[0]).join("+")
                        }
                      />
                    )}
                    <span className="mono tert" style={{ fontSize: 11 }}>
                      {e.from}
                    </span>
                  </div>
                  <div className="deltatag">
                    <CornerDownRight size={12} /> cleaned delta
                  </div>
                  <div className="delta">{R.delta}</div>
                  {R.facts.length > 0 && (
                    <div className="facts">
                      {R.facts.map((f, k) => {
                        const src = f[3] || "";
                        const isDocSrc = /Charter|SPA|IRA|ROFR|Voting|cap table|Term Sheet|Cap Table/i.test(src);
                        return (
                          <div className="fact" key={k}>
                            <div className="fk">{f[0]}</div>
                            <div className="fv">
                              {f[1]}
                              <Pill
                                color={
                                  f[2] === "conflict"
                                    ? "--red"
                                    : f[2] === "update"
                                      ? "--blue"
                                      : f[2] === "confirm"
                                        ? "--green"
                                        : "--amber"
                                }
                                label={f[2]}
                              />
                            </div>
                            {src && (
                              <div className="src">
                                {isDocSrc ? <FileText size={11} /> : <Inbox size={11} />}
                                From {src}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {R.tags.length > 0 && (
                    <div className="tagrow">
                      {R.tags.map((t, k) => {
                        const m = tagMeta(t[0]);
                        return <Pill key={k} color={m[0]} label={m[1]} />;
                      })}
                    </div>
                  )}
                  {(e.attachments || []).length > 0 && (
                    <div className="docsub">
                      <div className="docsubhd">
                        <Paperclip size={11} /> Documents · {e.attachments!.length}
                      </div>
                      {e.attachments!.map((a, k) => (
                        <div key={k} className="docsubrow">
                          <FileText size={11} color="var(--muted)" />
                          <span className="mono" style={{ fontSize: 11, color: "var(--fg)" }}>
                            {a.filename}
                          </span>
                          <span className="mono tert" style={{ fontSize: 10 }}>
                            {docTypeLabel(a.doc_type)} · v{a.version}
                          </span>
                          <span style={{ marginLeft: "auto" }}>
                            <DownloadLink doc={a} size={11} variant="sm" />
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="checkwrap">
                    {(() => {
                      const ranN = p.checks.filter((c) => !c.skipped).length;
                      const skipN = p.checks.length - ranN;
                      return (
                        <div
                          className="checkhd"
                          onClick={() => setShowChecks((s) => ({ ...s, [e.id + activeWs]: !sc }))}
                        >
                          {sc ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {ranN} checks ran
                          {skipN > 0 ? ` · ${skipN} skipped` : ""}
                        </div>
                      );
                    })()}
                    {sc && (
                      <div style={{ marginTop: 6 }}>
                        {p.checks.map((c) => {
                          const meta = CM[c.id] || ({ name: c.id, prompt: "" } as any);
                          const tau = thresholds[c.id] ?? (meta.tau || 0.6);
                          const key = e.id + c.id + activeWs;
                          const ex = expandedCheck[key];
                          const toggle = (): void =>
                            setExpandedCheck((s) => ({ ...s, [key]: !ex }));
                          if (c.skipped) {
                            return (
                              <div key={c.id}>
                                <div className="crow" style={{ opacity: 0.55 }}>
                                  <Pill color="--low" label="skip" />
                                  <span className="cn" onClick={toggle}>
                                    {meta.name}
                                  </span>
                                  <span
                                    className="tert"
                                    style={{
                                      fontSize: 11,
                                      marginLeft: "auto",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: 180,
                                    }}
                                  >
                                    {c.reason || "trigger did not fire"}
                                  </span>
                                  <span
                                    onClick={toggle}
                                    style={{ cursor: "pointer", color: "var(--tert)" }}
                                  >
                                    {ex ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  </span>
                                </div>
                                {ex && (
                                  <div className="cdetail">
                                    <div className="lbl">Skip reason</div>
                                    <div
                                      className="pp"
                                      style={{ whiteSpace: "normal", fontFamily: "var(--sans)", fontSize: 12 }}
                                    >
                                      {c.reason || "Trigger did not fire on this email."}
                                    </div>
                                    {meta.trigger && (
                                      <>
                                        <div className="lbl">Fires when</div>
                                        <div
                                          className="pp"
                                          style={{
                                            whiteSpace: "normal",
                                            fontFamily: "var(--sans)",
                                            fontSize: 12,
                                            marginBottom: 0,
                                          }}
                                        >
                                          {meta.trigger}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          /* Agent checks aren't pass/fail detectors — they ran
                             and produced work at some confidence. */
                          const isAgent = meta.kind === "workstream";
                          const st = statusOf(c.id, c.conf, tau);
                          const pill = c.ok === false || c.parked
                            ? { color: "--red", label: c.parked ? "parked" : "failed" }
                            : isAgent
                            ? c.conf >= tau
                              ? { color: "--green", label: "ran · " + c.conf.toFixed(2) }
                              : { color: "--amber", label: "low conf · " + c.conf.toFixed(2) }
                            : {
                                color: st === "pass" ? "--green" : st === "flag" ? "--amber" : "--red",
                                label: st,
                              };
                          return (
                            <div key={c.id}>
                              <div className="crow">
                                <Pill color={pill.color} label={pill.label} />
                                <span className="cn" onClick={toggle}>
                                  {meta.name}
                                </span>
                                <TauMeter tau={tau} conf={c.conf} />
                                <span
                                  onClick={toggle}
                                  style={{ cursor: "pointer", color: "var(--tert)" }}
                                >
                                  {ex ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </span>
                              </div>
                              {ex && (
                                <div className="cdetail">
                                  <div className="lbl">Prompt</div>
                                  <div className="pp">{meta.prompt}</div>
                                  {meta.threshold_rationale && (
                                    <>
                                      <div className="lbl">Why τ = {tau.toFixed(2)}</div>
                                      <div
                                        className="pp"
                                        style={{
                                          whiteSpace: "normal",
                                          fontFamily: "var(--sans)",
                                          fontSize: 12,
                                        }}
                                      >
                                        {meta.threshold_rationale}
                                      </div>
                                    </>
                                  )}
                                  <div className="lbl">Output</div>
                                  <div className="pp" style={{ marginBottom: 0, color: "var(--fg)" }}>
                                    {c.out}
                                  </div>
                                  {(() => {
                                    if (c.id === "participant") {
                                      /* Replays/restored snapshots/failed live checks carry
                                         no structured result — resolve against the roster. */
                                      const r = c.result as Record<string, unknown> | undefined;
                                      const hasData =
                                        !!r &&
                                        ((Array.isArray(r.mapped) && r.mapped.length > 0) ||
                                          (Array.isArray(r.unknown) && r.unknown.length > 0));
                                      const pres = hasData
                                        ? r
                                        : addressRoster
                                          ? synthParticipantResult(e, addressRoster)
                                          : null;
                                      return pres ? (
                                        <div style={{ marginTop: 6 }}>
                                          <ParticipantMap result={pres} entityNames={entityNames} />
                                        </div>
                                      ) : null;
                                    }
                                    return c.result ? (
                                      <div style={{ marginTop: 6 }}>
                                        <AgentResult id={c.id} result={c.result} />
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
